/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/count.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Returns the total number (count) of input values.
 *
 * @example
 * FROM employees
 * | STATS COUNT(height)
 *
 * @example
 * FROM employees
 * | STATS count = COUNT(*) BY languages
 * | SORT languages DESC
 *
 * @example
 * ROW words="foo;bar;baz;qux;quux;foo"
 * | STATS word_count = COUNT(SPLIT(words, ";"))
 *
 * @example
 * ROW n=1
 * | WHERE n < 0
 * | STATS COUNT(n)
 *
 * @example
 * FROM employees
 * | STATS
 *     gte20 = COUNT(*) WHERE height >= 2,
 *     lte18 = COUNT(*) WHERE height <= 1.8
 *
 * @example
 * ROW mv = [1, 2], n = NULL, t = TRUE, f = FALSE
 * | STATS COUNT(mv), COUNT(n), COUNT(t), COUNT(f)
 *
 * @example
 * ROW n=1
 * | STATS COUNT(n > 0 OR NULL), COUNT(n < 0 OR NULL)
 *
 * @example
 * TS exp_histo_sample
 * | WHERE instance == "instance-0"
 * | STATS cnt = COUNT(responseTime)
 *
 * @example
 * TS histogram_timeseries_index
 * | WHERE instance == "instance-0"
 * | STATS cnt = COUNT(responseTime::tdigest)
 */
const definition: FunctionDefinition = {
  type: 'agg',
  name: 'count',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'aggregate_metric_double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_range',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'dense_vector',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double_range',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'exponential_histogram',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'bucket',
          type: 'double_range',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohash',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohex',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geotile',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'tdigest',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'bucket',
          type: 'double_range',
          optional: true,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
